import { clampChoreDueOffsetDays, isChoreDueAnchor, type ChoreDueAnchorValue } from "@household/shared/chore-due-anchor";
import { browserTimezone, formValue, localDateOnly } from "../../app/helpers.js";
import type { ChoreOccurrence, EntityReferenceId } from "../../app/types.js";

/**
 * Field reader over a chore form. The DOM path reads from an HTMLFormElement;
 * tests build one from a plain map. Both feed the same payload builder so
 * creating a chore from the Chores module and from a Project produces identical
 * payloads.
 */
export interface ChoreFieldReader {
  has(name: string): boolean;
  get(name: string): string;
  getAll(name: string): string[];
}

function readerFromForm(form: HTMLFormElement): ChoreFieldReader {
  const data = new FormData(form);
  return {
    has: (name) => form.elements.namedItem(name) !== null || data.has(name),
    get: (name) => formValue(form, name),
    getAll: (name) => data.getAll(name).map((value) => String(value))
  };
}

/** Build a reader from a plain field map (single or repeated string values). */
export function readerFromValues(values: Record<string, string | string[]>): ChoreFieldReader {
  return {
    has: (name) => Object.prototype.hasOwnProperty.call(values, name),
    get: (name) => {
      const value = values[name];
      const single = Array.isArray(value) ? value[0] ?? "" : value ?? "";
      return single.trim();
    },
    getAll: (name) => {
      const value = values[name];
      if (Array.isArray(value)) return value;
      return value == null ? [] : [value];
    }
  };
}

function optionalId(value: string): EntityReferenceId | null {
  return value || null;
}

function optionalUuid(value: string): string | null {
  return value.trim() || null;
}

function formValueOr(reader: ChoreFieldReader, name: string, fallback = "") {
  if (!reader.has(name)) return fallback;
  return reader.get(name);
}

function optionalIdOr(reader: ChoreFieldReader, name: string, fallback?: EntityReferenceId | null) {
  if (!reader.has(name)) return fallback ?? null;
  return optionalId(reader.get(name));
}

function optionalUuidOr(reader: ChoreFieldReader, name: string, fallback?: string | null) {
  if (!reader.has(name)) return fallback ?? null;
  return optionalUuid(reader.get(name));
}

function weekdayFromDate(dateOnly: string): number {
  return new Date(`${dateOnly}T12:00:00`).getDay();
}

function recurrenceFromReader(reader: ChoreFieldReader, dueDate: string) {
  const recurrence = reader.get("recurrence");
  const interval = Math.max(1, Number(reader.get("repeatEvery") || 1));
  const endMode = reader.get("recurrenceEndMode");
  const endDate = endMode === "on" ? reader.get("recurrenceEndDate") : "";
  const occurrenceCount = endMode === "after" ? Math.max(1, Number(reader.get("recurrenceCount") || 1)) : undefined;
  const end = occurrenceCount ? { endDate, occurrenceCount } : { endDate };
  if (recurrence === "daily") return { frequency: "daily", interval, ...end };
  if (recurrence === "weekly") {
    const weekdays = reader.getAll("weekdays")
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);
    return {
      frequency: "weekly",
      interval,
      weekdays: weekdays.length ? weekdays : [weekdayFromDate(dueDate)],
      ...end
    };
  }
  if (recurrence === "monthly") return { frequency: "monthly", interval, dayOfMonth: Number(dueDate.slice(8, 10)), ...end };
  if (recurrence === "yearly") return { frequency: "monthly", interval: interval * 12, dayOfMonth: Number(dueDate.slice(8, 10)), ...end };
  if (recurrence === "interval_days") return { frequency: "interval_days", intervalDays: Number(reader.get("intervalDays") || 7), endDate: "" };
  return null;
}

export function nextSnoozeDate(effectiveDueDate?: string): string {
  const today = localDateOnly();
  const base = effectiveDueDate && effectiveDueDate > today ? effectiveDueDate : today;
  const next = new Date(`${base}T12:00:00`);
  next.setDate(next.getDate() + 1);
  return localDateOnly(next);
}

/**
 * The due-date half of the payload: the date itself, plus the sentence behind
 * it when the form is in relative mode (#714).
 *
 * The anchor pair is emitted ONLY when the form actually carries the field, for
 * the same reason `about` and `locationId` are gated above: a form that never
 * rendered the control (the inline description pencil, the maintenance editor)
 * must not send `dueAnchor: ""` and silently detach a chore from its project's
 * start date. Omission means "leave it alone" everywhere in this payload, and
 * this field is no exception.
 *
 * `dueDate` still travels beside the anchor, and is still the picker's own
 * resolved preview rather than anything computed here. The server re-resolves
 * from the project row it holds and stores ITS answer, so a stale project date
 * in this tab cannot write a wrong date; sending the preview only keeps the
 * optimistic row right for the moment before the refresh lands.
 *
 * An anchor without a project is dropped rather than sent: the server would
 * refuse it, and the composer can lose its project (the full editor's project
 * select is a live field) between the toggle being flipped and submit.
 */
function choreDueFromReader(
  reader: ChoreFieldReader,
  fallback: ChoreOccurrence | null | undefined,
  projectId: string | null
  // OPTIONAL properties rather than a union of two shapes: the caller spreads
  // this into the payload, and a union would make every reader of the result
  // narrow before it could see the other fields.
): { dueDate: string; anchor: { dueAnchor?: ChoreDueAnchorValue; dueOffsetDays?: number } } {
  const dueDate = formValueOr(reader, "dueDate", fallback?.dueDate ?? "");
  if (!reader.has("dueAnchor")) return { dueDate, anchor: {} };
  const raw = reader.get("dueAnchor");
  const dueAnchor: ChoreDueAnchorValue = projectId && isChoreDueAnchor(raw) ? raw : "";
  const dueOffsetDays = dueAnchor ? clampChoreDueOffsetDays(Number(reader.get("dueOffsetDays") || 0)) : 0;
  return { dueDate, anchor: { dueAnchor, dueOffsetDays } };
}

/** Pure chore payload builder shared by every entry point (chores create form,
 * chore details edit, and the project task composer). A project task may be
 * left undated (a planning to-do, #289) — only non-project chores fall back to
 * "due today" when the date is blank. */
export function buildChorePayload(reader: ChoreFieldReader, fallback?: ChoreOccurrence | null) {
  const projectId = optionalUuidOr(reader, "projectId", fallback?.projectId);
  const due = choreDueFromReader(reader, fallback, projectId);
  const rawDueDate = due.dueDate;
  const dueDate = rawDueDate || (projectId ? "" : localDateOnly());
  return {
    ...due.anchor,
    title: formValueOr(reader, "title", fallback?.title ?? ""),
    notes: formValueOr(reader, "notes", fallback?.notes ?? ""),
    priority: formValueOr(reader, "priority", fallback?.priority ?? "") || "normal",
    dueDate,
    dueTime: formValueOr(reader, "dueTime", fallback?.dueTime ?? ""),
    timezone: browserTimezone(),
    // Recurrence needs an anchor date; an undated task cannot repeat.
    recurrenceRule: !dueDate ? null : reader.has("recurrence") ? recurrenceFromReader(reader, dueDate) : fallback?.recurrenceRule ?? null,
    assignedToUserId: optionalUuidOr(reader, "assignedToUserId", fallback?.assignedToUserId),
    // Gated for the SAME reason `about` below is, and it has to be: a form that
    // renders neither control (the inline description pencil, say) used to send
    // `locationId: null` from a food-linked chore's fallback — `locationId` is
    // derived only for `entityType === "location"`, so it is null for a food —
    // and the server, seeing no `about` but a present `locationId`, wrote the
    // link away. Editing any unrelated field silently dropped the About link.
    // Sending nothing leaves the existing link alone, which is right for a place
    // link too: the fallback could only ever restate what the row already says.
    ...(reader.has("locationId") ? {
      locationId: optionalIdOr(reader, "locationId", fallback?.locationId)
    } : {}),
    // The discriminated About (#1047). Only emitted when the form HAS the
    // field, so a form that predates it cannot clobber an existing food link
    // by omission — the exact bug shape this issue exists to fix.
    ...(reader.has("aboutKind") ? {
      about: reader.get("aboutId")
        ? { kind: reader.get("aboutKind") === "product" ? "product" as const : "place" as const, id: reader.get("aboutId") }
        : null
    } : {}),
    categoryId: optionalIdOr(reader, "categoryId", fallback?.categoryId),
    projectId,
    estimatedMinutes: reader.has("estimatedMinutes") ? Number(reader.get("estimatedMinutes") || 0) : fallback?.estimatedMinutes ?? 0,
    ...(reader.has("tags") ? {
      tags: Array.from(new Set(reader.get("tags").split(",").map((tag) => tag.trim()).filter(Boolean)))
    } : {}),
    // Only send phase when the form has the field, so edit forms without it
    // don't clobber an existing phase.
    ...(reader.has("phase") ? { phase: reader.get("phase") } : {})
  };
}

export function chorePayload(form: HTMLFormElement, fallback?: ChoreOccurrence | null) {
  return buildChorePayload(readerFromForm(form), fallback);
}
